import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { API_BASE_URL } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import SEO from '../shared/SEO';
import LoadingScreen from '../shared/LoadingScreen';
import CharacterImageDisplay from './Display/CharacterImageDisplay';
import CharacterStatsDisplay from './Display/CharacterStatsDisplay';
import CharacterAptitudesDisplay from './Display/CharacterAptitudesDisplay';
import CharacterCardsDisplay from './Display/CharacterCardsDisplay';
import CharacterHeroTitle from './Display/CharacterHeroTitle';
import CostumeSkill from './Costumes/CostumeSkill';
import SearchCostumeSkill from './Costumes/SearchCostumeSkill';

const CharacterData = () => {
    const { characterName } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const isUserAdmin = isAdmin();
    const { t } = useTranslation();

    const [character, setCharacter] = useState(location.state?.character || null);
    const [cards, setCards] = useState([]);
    const [stats, setStats] = useState([]);
    const [aptitudes, setAptitudes] = useState([]); // Changed to array
    const [selectedCard, setSelectedCard] = useState(null);
    const [loading, setLoading] = useState(!character);

    // State for Skills Modal
    const [showSkillModal, setShowSkillModal] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        const fetchCharacterData = async () => {
            try {
                setLoading(true);

                // Si no tenemos el personaje en el state, lo buscamos
                let targetCharacter = character;

                if (!targetCharacter) {
                    const charsRes = await fetch(`${API_BASE_URL}/characters`);
                    const charactersData = await charsRes.json();

                    console.log(`[CharacterData] Fetched ${charactersData.length} characters.`);

                    // Helper for consistent slug generation
                    const normalizeSlug = (name) => name ? name.toLowerCase().trim().replace(/\s+/g, '-') : '';

                    // Buscar el personaje por el slug
                    targetCharacter = charactersData.find(c =>
                        normalizeSlug(c.name_en) === characterName
                    );

                    if (!targetCharacter) {
                        console.warn(`[CharacterData] Character not found for slug: "${characterName}"`);
                        console.log('Available slugs:', charactersData.map(c => normalizeSlug(c.name_en)).slice(0, 10), '...');
                    }
                }

                // Verificar si existe y si el usuario tiene permiso para verlo
                if (!targetCharacter || (targetCharacter.Player_type === 'NPC' && !isUserAdmin)) {
                    navigate(ROUTES.CHARACTERS_ORIGINAL);
                    return;
                }

                if (!character) {
                    setCharacter(targetCharacter);
                }

                // Cargar datos relacionados
                const [cardsRes, statsRes, aptsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/cards`),
                    fetch(`${API_BASE_URL}/stats`),
                    fetch(`${API_BASE_URL}/aptitudes`)
                ]);

                const cardsData = await cardsRes.json();
                const statsData = await statsRes.json();
                const aptitudesData = await aptsRes.json();

                // Filtrar por el personaje actual
                const charId = targetCharacter?.Id_Personaje;

                const filteredCards = cardsData.filter(c => c.Id_Personaje === charId);
                const filteredStats = statsData.filter(s => s.Id_Personaje === charId).sort((a, b) => a.Estrellas - b.Estrellas);

                setCards(filteredCards);
                setStats(filteredStats);
                // Store all aptitudes for this character (array)
                const charAptitudes = aptitudesData.filter(a => a.Id_Personaje === charId);
                setAptitudes(charAptitudes);

                // Seleccionar el traje "Original" si existe
                const originalCard = filteredCards.find(c => c.reference === 'Original');
                setSelectedCard(originalCard || filteredCards[0] || null);

            } catch (error) {
                console.error('Error fetching character data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCharacterData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [characterName]);

    // Logic to select aptitudes based on current card
    const currentAptitudes = (() => {
        if (!selectedCard) return {};

        // 1. Precise Match: Look for aptitude with same card_id
        // eslint-disable-next-line eqeqeq
        const exactMatch = aptitudes.find(a => a.card_id != null && a.card_id == selectedCard.card_id);
        if (exactMatch) return exactMatch;

        // 2. Fallback: Look for aptitude with matching Id_Personaje (and no card_id or card_id=null)
        // This covers legacy data or cache misses
        const fallback = aptitudes.find(a =>
            // eslint-disable-next-line eqeqeq
            a.Id_Personaje == character?.Id_Personaje &&
            (a.card_id == null || a.card_id === undefined)
        );

        return fallback || {};
    })();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!character) {
        return (
            <div className="min-h-screen bg-panel-gray pt-20 pb-24 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600 text-lg mb-4">{t('CharacterData.error_not_found')}</p>
                    <button
                        onClick={() => navigate(ROUTES.CHARACTERS_ORIGINAL)}
                        className="px-4 py-2 bg-primary-green text-white rounded-lg hover:bg-green-600"
                    >
                        {t('CharacterData.button_back')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-panel-gray pt-20 pb-24">
            <SEO title={character.name_en} description={`Datos, estadísticas y cartas de ${character.name_en}`} />
            <div className="max-w-7xl mx-auto p-6">
                {/* Header con botón de volver */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(ROUTES.CHARACTERS_ORIGINAL)}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        {t('CharacterData.button_back')}
                    </button>

                    <div className="flex justify-between items-start">
                        <CharacterHeroTitle character={character} />
                        {isUserAdmin && (
                            <button
                                onClick={() => navigate(`${ROUTES.CHARACTER}/${character.Id_Personaje}/edit`)}
                                className="bg-secondary-blue text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 transition-colors flex items-center gap-2 shadow-sm"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                                {t('CharacterData.button_edit')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Contenido principal */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna izquierda: Imagen */}
                    <div className="lg:col-span-1">
                        <CharacterImageDisplay
                            character={character}
                            card={selectedCard}
                        />
                    </div>

                    {/* Columna derecha: Tabs, Card Info, Aptitudes, Stats, Skills */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Tabs de Costumes */}
                        {cards.length > 0 && (
                            <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
                                {cards.map(card => (
                                    <button
                                        key={card.card_id}
                                        onClick={() => setSelectedCard(card)}
                                        className={`px-4 py-2 rounded-t-lg font-medium transition-all ${selectedCard?.card_id === card.card_id
                                            ? 'bg-primary-green text-white shadow-md transform -translate-y-1'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {card.reference || card.title_en}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* 1. Card Info */}
                        <CharacterCardsDisplay
                            character={character}
                            selectedCard={selectedCard}
                        />

                        <CharacterAptitudesDisplay
                            aptitudes={currentAptitudes}
                        />

                        {/* 3. Stats (Filtered by Costume) */}
                        <CharacterStatsDisplay
                            stats={stats.filter(s => s.card_id === selectedCard?.card_id)}
                        />

                        {/* 4. Skills (Dependent on Costume) */}
                        {selectedCard && (
                            <div className="bg-card-white rounded-2xl shadow-xl p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-2xl font-bold text-gray-800">{t('CharacterData.section_skills')}</h2>
                                    {isUserAdmin && (
                                        <button
                                            onClick={() => setShowSkillModal(true)}
                                            className="px-3 py-1.5 bg-secondary-blue text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            {t('CharacterData.button_add_skill')}
                                        </button>
                                    )}
                                </div>

                                <CostumeSkill
                                    cardId={selectedCard.card_id}
                                    refreshTrigger={refreshTrigger}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal para agregar Skills */}
            {showSkillModal && selectedCard && (
                <SearchCostumeSkill
                    isOpen={showSkillModal}
                    onClose={() => setShowSkillModal(false)}
                    cardId={selectedCard.card_id}
                    onAddSkill={() => setRefreshTrigger(prev => prev + 1)}
                />
            )}
        </div>
    );
};

export default CharacterData;
// Revisado: 2025-12-18 [I18n Scan]
